import { Box, useTheme } from '@mui/material';
import _ from 'lodash';
import { digitalBalanceRequestsTableTypes, digitalBalanceTableColumnsConfig } from './tables.types';
import { columnType } from 'components/table/types';
import { formatNumber } from 'components/FormCreatorComponent/helpers';

// Helper function to get company name by ID (matching pending requests pattern)
const getCompanyName = (companyId: number, companies: Record<string, any>[] = []) => {
	const companiesObj = _.keyBy(companies, 'id');
	const company = _.get(companiesObj, String(companyId));
	return company?.hebrew_name || company?.companyName || company?.name || '';
};

// Data transformation function
export const tableData = (
	type: digitalBalanceRequestsTableTypes,
	data: Record<string, any>[],
	t: any,
	companies: Record<string, any>[] = []
) => {
	return data.map((item) => ({
		id: item.id,
		companyName: getCompanyName(item?.companyId, companies),
		customerName: `${item?.createdBy?.firstNameHebrew || ''} ${item?.createdBy?.lastNameHebrew || ''}`.trim(),
		createdAt: item?.createdAt,
		feezbackUpdatedAt: item?.feezbackUpdatedAt || '',
		amount: item?.amount,
		clientBankAccount: `${item?.bankAccount?.bankName?.bankName || ''} , ${item?.bankAccount?.bankBranch || ''}, ${
			item?.bankAccount?.accountNumber || ''
		}`,
		altshulerBankAccount: `${item?.altshulerAccount?.bank?.bankName || ''} , ${
			item?.altshulerAccount?.bankBranch || ''
		}, ${item?.altshulerAccount?.accountNumber || ''}`,
		hyperAccountNumber: item?.company?.hyperAccountNumber || '',
		documentedOnHyperAction: item?.documentedOnHyper,
		documentedOnHyperDate: item?.hyperDocumentedAt,
		documentedByBoUser: item?.documentedByBoUser
			? `${item.documentedByBoUser.firstName || ''} ${item.documentedByBoUser.lastName || ''}`.trim()
			: '',
		currency: item?.currency,
		feezbackBankReferenceNumber: item?.feezbackBankReferenceNumber || '',
	}));
};

// Column building function
export const buildColumns = (
	type: digitalBalanceRequestsTableTypes,
	t: any,
	onDocumentedActionClick?: (requestId: number) => void,
	columnsToShow?: string[],
	sortableColumns?: string[]
): columnType[] => {
	const columns: columnType[] = [];
	const tableColumns = digitalBalanceTableColumnsConfig[type];
	const theme = useTheme();

	Object.entries(tableColumns).forEach(([element, columnType]) => {
		// Skip if column is not in columnsToShow list
		if (columnsToShow && !columnsToShow.includes(element)) {
			return;
		}

		let customValueHandle: any = {};

		// Handle amount formatting
		if (element === 'amount') {
			customValueHandle = {
				customValueHandle: (value: any, row: Record<string, any>) => {
					return value ? `${formatNumber(value)} ${row?.currency?.symbol || ''}` : '';
				},
			};
		}

		// Handle documented on hyper action button
		if (element === 'documentedOnHyperAction') {
			customValueHandle = {
				customValueHandle: (value: any, row: Record<string, any>) => {
					// Only show button for non-documented requests
					if (!value && onDocumentedActionClick) {
						return (
							<button
								onClick={() => {
									onDocumentedActionClick(row.id);
								}}
								style={{
									padding: '10px',
									backgroundColor: theme.palette.root.basicGreen,
									color: 'white',
									border: 'none',
									borderRadius: '4px',
									cursor: 'pointer',
								}}
							>
								{t('page.digitalBalanceRequests.field.approve.documentedOnHyper')}
							</button>
						);
					}
					// For documented requests, show status text
					return <span></span>;
				},
			};
		}

		const props: columnType = {
			fieldId: 'id',
			name: element,
			title: t(`page.digitalBalanceRequests.field.${element}`),
			sortable: sortableColumns ? sortableColumns.includes(element) : false,
			type: columnType,
			...customValueHandle,
		};

		columns.push(props);
	});

	return columns;
};
